import React, { useState } from 'react';
import { PersonDetailData } from '../index';
import { MessageSquare, Plus, X, CheckCircle, AlertCircle, Loader, Clock, User } from 'lucide-react';
import { useCustomerNotes } from '../../../hooks/useCustomerNotes';
import { cn } from '../../../utils/cn';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface NotesTabProps {
  person: PersonDetailData;
  darkMode?: boolean;
}

export const NotesTab: React.FC<NotesTabProps> = ({ person, darkMode = false }) => {
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [concludingNoteId, setConcludingNoteId] = useState<string | null>(null);
  const [concludeReason, setConcludeReason] = useState('');
  const [showConcludeModal, setShowConcludeModal] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [showConcluded, setShowConcluded] = useState(false);

  const customerId = person.customer?.id;
  const personId = person.id;

  // Fetch person-specific notes (all notes - both active and concluded)
  const { notes, loading, error, createNote, concludeNote, refreshNotes } = useCustomerNotes(
    customerId,
    { scope: { person_id: personId }, activeOnly: false } // Fetch ALL notes (active + concluded)
  );

  // Filter notes based on user preference
  const filteredNotes = showConcluded
    ? notes // Show all notes
    : notes.filter(note => note.is_active); // Show only active notes

  const activeCount = notes.filter(n => n.is_active).length;
  const concludedCount = notes.filter(n => !n.is_active).length;

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      toast.error('Por favor, escreva uma nota');
      return;
    }

    setIsAddingNote(true);
    try {
      await createNote(newNoteContent.trim());
      setNewNoteContent('');
      toast.success('Nota adicionada com sucesso');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao adicionar nota');
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleConcludeClick = (noteId: string) => {
    setSelectedNoteId(noteId);
    setShowConcludeModal(true);
  };

  const handleConcludeNote = async () => {
    if (!selectedNoteId) return;

    setConcludingNoteId(selectedNoteId);
    try {
      await concludeNote(selectedNoteId, concludeReason.trim() || undefined);
      toast.success('Nota concluída');
      setShowConcludeModal(false);
      setConcludeReason('');
      setSelectedNoteId(null);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao concluir nota');
    } finally {
      setConcludingNoteId(null);
    }
  };

  return (
    <div className={cn("space-y-4", darkMode ? "text-gray-100" : "text-gray-900")}>
      {/* Header */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          {loading && (
            <Loader className="w-4 h-4 animate-spin text-blue-500" />
          )}
          {/* Status Filter Toggle */}
          <div className={cn(
            "flex items-center gap-1 p-1 rounded-lg",
            darkMode ? "bg-gray-800" : "bg-gray-200"
          )}>
            <button
              onClick={() => setShowConcluded(false)}
              className={cn(
                "px-3 py-1 text-xs font-medium rounded transition-colors",
                !showConcluded
                  ? darkMode
                    ? "bg-blue-600 text-white"
                    : "bg-blue-600 text-white"
                  : darkMode
                    ? "text-gray-400 hover:text-gray-300"
                    : "text-gray-600 hover:text-gray-900"
              )}
            >
              Ativas ({activeCount})
            </button>
            <button
              onClick={() => setShowConcluded(true)}
              className={cn(
                "px-3 py-1 text-xs font-medium rounded transition-colors",
                showConcluded
                  ? darkMode
                    ? "bg-blue-600 text-white"
                    : "bg-blue-600 text-white"
                  : darkMode
                    ? "text-gray-400 hover:text-gray-300"
                    : "text-gray-600 hover:text-gray-900"
              )}
            >
              Todas ({notes.length})
            </button>
          </div>
        </div>
      </div>

      {/* Description */}
      <p className={cn("text-sm", darkMode ? "text-gray-400" : "text-gray-600")}>
        Adicione notas e alertas sobre esta pessoa para comunicação interna da equipe.
      </p>

      {/* Add Note Form */}
      <div className={cn(
        "p-4 rounded-lg border-2 border-dashed",
        darkMode ? "bg-gray-800/50 border-gray-700" : "bg-gray-50 border-gray-300"
      )}>
        <textarea
          value={newNoteContent}
          onChange={(e) => setNewNoteContent(e.target.value)}
          placeholder="Escreva uma nota ou alerta sobre esta pessoa..."
          rows={3}
          className={cn(
            "w-full px-3 py-2 rounded-lg border resize-none focus:outline-none focus:ring-2",
            darkMode
              ? "bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500 focus:ring-blue-500"
              : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500"
          )}
        />
        <div className="flex items-center justify-between mt-3">
          <div className={cn("text-xs", darkMode ? "text-gray-500" : "text-gray-500")}>
            <span>Esta nota será vinculada à pessoa {person.first_name} {person.last_name}</span>
          </div>
          <button
            onClick={handleAddNote}
            disabled={!newNoteContent.trim() || isAddingNote}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors",
              darkMode
                ? "bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-700 disabled:text-gray-500"
                : "bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:text-gray-500"
            )}
          >
            {isAddingNote ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Adicionar Nota
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className={cn(
          "p-3 rounded-lg border flex items-center gap-2",
          darkMode
            ? "bg-red-900/20 border-red-800 text-red-400"
            : "bg-red-50 border-red-200 text-red-700"
        )}>
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Notes List */}
      <div className="space-y-3">
        {filteredNotes.length === 0 ? (
          <div className={cn(
            "text-center py-8 rounded-lg border-2 border-dashed",
            darkMode ? "border-gray-700 text-gray-500" : "border-gray-300 text-gray-400"
          )}>
            <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="font-medium">
              {showConcluded ? 'Nenhuma nota encontrada' : 'Nenhuma nota ativa'}
            </p>
            <p className="text-sm mt-1">
              {showConcluded
                ? `Adicione a primeira nota sobre ${person.first_name}`
                : concludedCount > 0
                  ? `Existem ${concludedCount} nota(s) concluída(s)`
                  : `Adicione a primeira nota sobre ${person.first_name}`
              }
            </p>
          </div>
        ) : (
          filteredNotes.map((note) => (
            <div
              key={note.id}
              className={cn(
                "p-4 rounded-lg border",
                darkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-white border-gray-200"
              )}
            >
              {/* Note Header */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <User className={cn("w-4 h-4 flex-shrink-0", darkMode ? "text-gray-400" : "text-gray-500")} />
                  <span className={cn("text-sm font-medium truncate", darkMode ? "text-gray-300" : "text-gray-700")}>
                    {note.created_by_name}
                  </span>
                  {/* Status Badge */}
                  {!note.is_active && (
                    <span className={cn(
                      "text-xs px-2 py-0.5 rounded-full flex-shrink-0",
                      darkMode
                        ? "bg-green-900/30 text-green-400 border border-green-700"
                        : "bg-green-100 text-green-700 border border-green-300"
                    )}>
                      Concluída
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className={cn(
                    "text-xs flex items-center gap-1",
                    darkMode ? "text-gray-500" : "text-gray-400"
                  )}>
                    <Clock className="w-3 h-3" />
                    {formatDistanceToNow(new Date(note.created_at), { addSuffix: true, locale: ptBR })}
                  </div>
                  {note.is_active && (
                    <button
                      onClick={() => handleConcludeClick(note.id)}
                      disabled={concludingNoteId === note.id}
                      className={cn(
                        "p-1 rounded hover:bg-opacity-10 transition-colors",
                        darkMode
                          ? "hover:bg-green-500 text-gray-400 hover:text-green-400"
                          : "hover:bg-green-500 text-gray-500 hover:text-green-600"
                      )}
                      title="Concluir nota"
                    >
                      {concludingNoteId === note.id ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Note Content */}
              <p className={cn(
                "text-sm whitespace-pre-wrap",
                darkMode ? "text-gray-300" : "text-gray-700",
                !note.is_active && "opacity-75"
              )}>
                {note.content}
              </p>

              {/* Concluded Info */}
              {!note.is_active && note.concluded_by_name && (
                <div className={cn(
                  "mt-2 pt-2 border-t text-xs",
                  darkMode ? "border-gray-700 text-gray-500" : "border-gray-200 text-gray-600"
                )}>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    <span>Concluída por {note.concluded_by_name}</span>
                    {note.concluded_at && (
                      <span>• {formatDistanceToNow(new Date(note.concluded_at), { addSuffix: true, locale: ptBR })}</span>
                    )}
                  </div>
                  {note.concluded_reason && (
                    <p className="mt-1 italic">Motivo: {note.concluded_reason}</p>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Conclude Note Modal */}
      {showConcludeModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className={cn(
            "w-full max-w-md rounded-lg shadow-xl p-6",
            darkMode ? "bg-gray-800" : "bg-white"
          )}>
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className={cn("w-5 h-5", darkMode ? "text-green-400" : "text-green-600")} />
              <h3 className={cn("font-semibold text-lg", darkMode ? "text-gray-100" : "text-gray-900")}>
                Concluir Nota
              </h3>
            </div>

            <p className={cn("text-sm mb-4", darkMode ? "text-gray-400" : "text-gray-600")}>
              Deseja adicionar um motivo para a conclusão desta nota? (opcional)
            </p>

            <textarea
              value={concludeReason}
              onChange={(e) => setConcludeReason(e.target.value)}
              placeholder="Motivo da conclusão (opcional)..."
              rows={3}
              className={cn(
                "w-full px-3 py-2 rounded-lg border resize-none focus:outline-none focus:ring-2 mb-4",
                darkMode
                  ? "bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500 focus:ring-green-500"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-green-500"
              )}
            />

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowConcludeModal(false);
                  setConcludeReason('');
                  setSelectedNoteId(null);
                }}
                className={cn(
                  "flex-1 px-4 py-2 rounded-lg font-medium transition-colors",
                  darkMode
                    ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                )}
              >
                Cancelar
              </button>
              <button
                onClick={handleConcludeNote}
                disabled={concludingNoteId !== null}
                className={cn(
                  "flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2",
                  darkMode
                    ? "bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-700 disabled:text-gray-500"
                    : "bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-300 disabled:text-gray-500"
                )}
              >
                {concludingNoteId ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4" />
                )}
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
